import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ThumbsUp, Share2, MessageSquare } from 'lucide-react';
import VideoCard from '../components/VideoCard';
import './VideoPlayer.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function VideoPlayer() {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [commentAuthor, setCommentAuthor] = useState('');

  useEffect(() => {
    fetchVideo();
    fetchRelatedVideos();
  }, [id]);

  const fetchVideo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/videos/${id}`);
      setVideo(response.data);
    } catch (err) {
      console.error('Error fetching video:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedVideos = async () => {
    try {
      const response = await axios.get('/api/videos');
      setRelatedVideos(response.data.filter(v => v.id !== id).slice(0, 10));
    } catch (err) {
      console.error('Error fetching related videos:', err);
    }
  };

  const handleLike = async () => {
    try {
      const response = await axios.post(`/api/videos/${id}/like`);
      setVideo({ ...video, likes: response.data.likes });
    } catch (err) {
      console.error('Error liking video:', err);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    try {
      const response = await axios.post(`/api/videos/${id}/comment`, {
        text: comment,
        author: commentAuthor || 'Anonymous'
      });
      setVideo({
        ...video,
        comments: [...video.comments, response.data]
      });
      setComment('');
    } catch (err) {
      console.error('Error posting comment:', err);
    }
  };

  const formatViews = (views) => {
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!video) {
    return <div className="error-container">Video not found</div>;
  }

  const videoSrc =
    video.videoUrl && video.videoUrl.startsWith('http')
      ? video.videoUrl
      : `${API_URL}${video.videoUrl}`;

  return (
    <div className="video-player-page">
      <div className="video-player-container">
        <div className="video-player-main">
          <div className="video-wrapper">
            <video controls className="video-element" key={video.videoUrl}>
              <source src={videoSrc} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          <div className="video-info-section">
            <h1 className="video-title-large">{video.title}</h1>
            
            <div className="video-stats">
              <span>{formatViews(video.views)} views • {formatDate(video.uploadDate)}</span>
              <div className="video-actions">
                <button className="action-btn" onClick={handleLike}>
                  <ThumbsUp size={20} />
                  <span>{video.likes}</span>
                </button>
                <button className="action-btn">
                  <Share2 size={20} />
                  <span>Share</span>
                </button>
              </div>
            </div>

            <div className="channel-info">
              <img src={video.channelAvatar} alt={video.channel} className="channel-avatar-large" />
              <div className="channel-details">
                <h3>{video.channel}</h3>
              </div>
            </div>

            <div className="video-description">
              <p>{video.description}</p>
            </div>
          </div>

          <div className="comments-section">
            <h3 className="comments-title">
              <MessageSquare size={24} />
              {video.comments.length} Comments
            </h3>

            <form className="comment-form" onSubmit={handleComment}>
              <input
                type="text"
                placeholder="Your name (optional)"
                className="input"
                value={commentAuthor}
                onChange={(e) => setCommentAuthor(e.target.value)}
                style={{ marginBottom: '8px' }}
              />
              <textarea
                placeholder="Add a comment..."
                className="comment-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows="3"
              />
              <button type="submit" className="btn btn-primary">
                Comment
              </button>
            </form>

            <div className="comments-list">
              {video.comments.map((comment) => (
                <div key={comment.id} className="comment">
                  <img src={comment.avatar} alt={comment.author} className="comment-avatar" />
                  <div className="comment-content">
                    <div className="comment-header">
                      <span className="comment-author">{comment.author}</span>
                      <span className="comment-date">{formatDate(comment.date)}</span>
                    </div>
                    <p className="comment-text">{comment.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="related-videos">
          <h3 className="related-title">Related Videos</h3>
          <div className="related-videos-list">
            {relatedVideos.map((relatedVideo) => (
              <VideoCard key={relatedVideo._id} video={relatedVideo} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoPlayer;
